import { useCallback, useRef } from 'react'
import { applyNodeChanges, useReactFlow } from '@/libs/workflow-node-ui'
import type { NodeChange } from '@/libs/workflow-node-ui'
import type { CanvasNode } from '../application/workflow-canvas-layout'
import { useSelector } from '@/libs/state'
import { activeWorkflow$, canvasEdges$, canvasNodes$ } from '../store'
import { useCanvasLayout } from './use-canvas-layout'
import { useCanvasPositions } from './use-canvas-positions'

const useWorkflowCanvas = () => {
	const nodes = useSelector(canvasNodes$)
	const edges = useSelector(canvasEdges$)

	const { applyLayout, recalculate } = useCanvasLayout()
	const { persistPositions, clearPositions, storedPositionsFor } = useCanvasPositions()
	const { fitView } = useReactFlow()

	const measuredRef = useRef(false)

	// Called when the active workflow changes identity - full reset
	const initLayout = useCallback(() => {
		const wf = activeWorkflow$.get()
		if (!wf) return
		measuredRef.current = false
		applyLayout(storedPositionsFor(wf.id))
	}, [applyLayout, storedPositionsFor])

	// Called after a data refresh on the same workflow - recomputes nodes+edges using
	// measured sizes so new nodes appear at computed positions without moving existing ones
	const refreshLayout = useCallback(() => {
		const wf = activeWorkflow$.get()
		if (!wf) return
		recalculate(storedPositionsFor(wf.id))
	}, [recalculate, storedPositionsFor])

	const resetPositions = useCallback(() => {
		const wf = activeWorkflow$.get()
		if (!wf) return
		clearPositions()
		applyLayout({})
		measuredRef.current = false
		requestAnimationFrame(() => {
			recalculate({})
			requestAnimationFrame(() => fitView({ padding: 0.2 }))
		})
	}, [clearPositions, applyLayout, recalculate, fitView])

	const onNodesChange = useCallback((changes: NodeChange<CanvasNode>[]) => {
		const updated = applyNodeChanges<CanvasNode>(changes, canvasNodes$.get())
		canvasNodes$.set(updated)

		const hasDrag = changes.some(c => c.type === 'position' && c.dragging === false)
		if (hasDrag) persistPositions(updated)

		if (measuredRef.current) return
		const allMeasured = changes.every(c => c.type !== 'dimensions' || c.dimensions !== undefined)
		if (!allMeasured) return

		measuredRef.current = true
		const wf = activeWorkflow$.get()
		if (wf) recalculate(storedPositionsFor(wf.id))
	}, [persistPositions, recalculate, storedPositionsFor])

	return { nodes, edges, onNodesChange, initLayout, refreshLayout, resetPositions }
}

export { useWorkflowCanvas }
